import { defineModule } from '@/js/utils/helpers';

interface FaqI18n {
  /** [singular, plural] — "%d vraag in %d categorieën" */
  totals: [string, string];
  /** [singular, plural] — "%d vraag gevonden voor “%s”" */
  results: [string, string];
  /** [singular, plural] — "%d vraag" */
  sectionCount: [string, string];
  /** "Geen vraag gevonden voor “%s”" */
  emptyHeading: string;
}

interface FaqSection {
  element: HTMLElement;
  link: HTMLElement | null;
  linkCount: HTMLElement | null;
  count: HTMLElement | null;
  items: FaqItem[];
}

interface FaqItem {
  element: HTMLElement;
  heading: HTMLElement | null;
  answer: HTMLElement | null;
  headingHtml: string;
  answerHtml: string;
  haystack: string;
}

/** Distance from the top at which a category counts as the one being read. */
const SPY_OFFSET = 140;

/**
 * Fill the placeholders the PHP side wrote. Numbered ones (`%1$s`) index into
 * `values`, so a translation may reorder them; bare ones consume in order.
 */
const format = (
  template: string,
  ...values: Array<string | number>
): string => {
  const queue = [...values];

  return template.replace(
    /%(?:(\d+)\$)?([ds])/g,
    (_match, position: string | undefined) =>
      String((position ? values[Number(position) - 1] : queue.shift()) ?? ''),
  );
};

/** Pick singular or plural on `count`, then fill the placeholders. */
const countLabel = (
  forms: [string, string],
  count: number,
  ...rest: Array<string | number>
): string => format(forms[count === 1 ? 0 : 1], count, ...rest);

/**
 * Reset an element to `html` and wrap the first occurrence of `term` in a
 * <mark>. The term is matched per text node, so rich text keeps its markup.
 */
const markTerm = (element: HTMLElement, html: string, term: string): void => {
  element.innerHTML = html;

  if (!term) return;

  const needle = term.toLowerCase();
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);

  for (
    let node = walker.nextNode() as Text | null;
    node;
    node = walker.nextNode() as Text | null
  ) {
    const at = node.data.toLowerCase().indexOf(needle);

    if (at >= 0) {
      const hit = node.splitText(at);
      hit.splitText(term.length);

      const mark = document.createElement('mark');
      mark.textContent = hit.data;
      hit.replaceWith(mark);

      return;
    }
  }
};

const setOpen = (element: HTMLElement, open: boolean): void => {
  const content = element.querySelector('.accordion__content');

  element.classList.toggle('accordion--open', open);
  element
    .querySelector('.accordion__trigger')
    ?.setAttribute('aria-expanded', open.toString());
  content?.setAttribute('aria-hidden', (!open).toString());
  content?.toggleAttribute('inert', !open);
};

/**
 * Wire up the overview page: client-side filtering over the questions already
 * in the DOM, live counters, and a category column that follows along while
 * scrolling. Returns a teardown function.
 */
const setupOverview = (root: HTMLElement): (() => void) | null => {
  const input = root.querySelector<HTMLInputElement>('[data-faq-search]');
  const counter = root.querySelector<HTMLElement>('[data-faq-counter]');
  const empty = root.querySelector<HTMLElement>('[data-faq-empty]');
  const emptyHeading = root.querySelector<HTMLElement>(
    '[data-faq-empty-heading]',
  );
  const clearButton = root.querySelector<HTMLElement>('[data-faq-clear]');
  const dataElement = root.querySelector<HTMLScriptElement>('[data-faq-i18n]');

  if (!input || !counter || !dataElement) return null;

  let i18n: FaqI18n;
  try {
    i18n = JSON.parse(dataElement.textContent || '') as FaqI18n;
  } catch {
    return null;
  }

  const sections: FaqSection[] = Array.from(
    root.querySelectorAll<HTMLElement>('[data-faq-section]'),
  ).map((element) => {
    const link = root.querySelector<HTMLElement>(
      `[data-faq-cat="${CSS.escape(element.dataset.faqSection ?? '')}"]`,
    );

    return {
      element,
      link,
      linkCount:
        link?.querySelector<HTMLElement>('[data-faq-cat-count]') ?? null,
      count: element.querySelector<HTMLElement>('[data-faq-section-count]'),
      items: Array.from(
        element.querySelectorAll<HTMLElement>('.accordion'),
      ).map((item) => {
        const heading = item.querySelector<HTMLElement>('.accordion__question');
        const answer = item.querySelector<HTMLElement>(
          '.accordion__content .content',
        );

        return {
          element: item,
          heading,
          answer,
          headingHtml: heading?.innerHTML ?? '',
          answerHtml: answer?.innerHTML ?? '',
          haystack: (item.textContent ?? '').toLowerCase(),
        };
      }),
    };
  });

  if (!sections.length) return null;

  const total = sections.reduce(
    (sum, section) => sum + section.items.length,
    0,
  );

  // Which question to return to once the filter is cleared. Read from the DOM
  // rather than tracked, so a question the visitor opened by hand wins over the
  // one the template rendered open.
  let restoreTo = root.querySelector<HTMLElement>('.accordion--open');
  let term = '';

  const render = (): void => {
    const needle = term.toLowerCase();
    let hits = 0;
    let visibleSections = 0;

    sections.forEach((section) => {
      let matches = 0;

      section.items.forEach((item) => {
        const match = !needle || item.haystack.includes(needle);

        item.element.classList.toggle('hidden', !match);

        const hitTerm = match ? term : '';

        if (item.heading) markTerm(item.heading, item.headingHtml, hitTerm);
        if (item.answer) markTerm(item.answer, item.answerHtml, hitTerm);

        // Every hit opens while searching: with a filter active the visitor is
        // reading answers, not browsing headings.
        if (term) setOpen(item.element, match);
        if (match) matches += 1;
      });

      section.element.classList.toggle('hidden', matches === 0);

      if (section.count) {
        section.count.textContent = countLabel(i18n.sectionCount, matches);
      }
      if (section.linkCount) section.linkCount.textContent = String(matches);
      section.link?.classList.toggle('is-dimmed', Boolean(term) && !matches);

      hits += matches;
      if (matches) visibleSections += 1;
    });

    counter.textContent = term
      ? countLabel(i18n.results, hits, term)
      : countLabel(i18n.totals, total, visibleSections);

    empty?.classList.toggle('hidden', hits > 0);

    if (emptyHeading && !hits) {
      emptyHeading.textContent = format(i18n.emptyHeading, term);
    }
  };

  const clear = (): void => {
    if (!term) return;

    term = '';
    input.value = '';
    render();

    // Collapse everything the filter opened and restore the single question that
    // was open before searching started.
    sections.forEach((section) =>
      section.items.forEach((item) => setOpen(item.element, false)),
    );
    if (restoreTo) setOpen(restoreTo, true);
  };

  const onInput = (): void => {
    if (!term) restoreTo = root.querySelector<HTMLElement>('.accordion--open');

    const next = input.value.trim();
    if (next === term) return;

    if (!next) {
      clear();
      return;
    }

    term = next;
    render();
  };

  const onScroll = (): void => {
    if (term) return;

    let active = sections[0];

    sections.forEach((section) => {
      if (section.element.getBoundingClientRect().top <= SPY_OFFSET) {
        active = section;
      }
    });

    sections.forEach((section) =>
      section.link?.classList.toggle('is-active', section === active),
    );
  };

  // A category link is a jump, not a filter — clear the search so the section it
  // points at is actually on the page.
  const onNavClick = (event: Event): void => {
    if ((event.target as HTMLElement).closest('[data-faq-cat]')) clear();
  };

  input.addEventListener('input', onInput);
  input.addEventListener('search', onInput);
  clearButton?.addEventListener('click', clear);
  root.addEventListener('click', onNavClick);
  window.addEventListener('scroll', onScroll, { passive: true });

  // A browser that restored the field on a back navigation starts out filtered.
  if (input.value.trim()) onInput();
  onScroll();

  return () => {
    input.removeEventListener('input', onInput);
    input.removeEventListener('search', onInput);
    clearButton?.removeEventListener('click', clear);
    root.removeEventListener('click', onNavClick);
    window.removeEventListener('scroll', onScroll);
  };
};

let cleanups: Array<() => void> = [];

export default defineModule(
  () => {
    cleanups = Array.from(
      document.querySelectorAll<HTMLElement>('[data-faq-overview]'),
    )
      .map(setupOverview)
      .filter((fn): fn is () => void => typeof fn === 'function');
  },
  () => {
    cleanups.forEach((fn) => fn());
    cleanups = [];
  },
);
